import {
  Box,
  Typography,
  Tabs,
  Tab,
  Button,
  Menu,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
} from "@mui/material";
import React, { useState } from "react";
import { useLocation } from "react-router-dom";
import CustomBreadcrumbs from "../../components-new/common/CustomBreadCrums";
import { PageTitle } from "../../components-new/common";
import { useBenchmarkResultsQuery, useGetUIDesignReportQuery } from "../../useQuery";

import SmokeTestResults from "./SmokeTestResults";
import MilestonePerformance from "./MilestonePerformance";
import SummaryReport from "./SummaryReport";
import MultiRunTable from "./MultiRunTable";
import DesignPrincipalTable from "./DesignPrincipalTable";
import { useDownloadReport } from "../../useQuery";

const BenchmarkReport = () => {
  const [tab, setTab] = useState(0);

  const location = useLocation();
  const { mutate: downloadReport, isPending: loading } = useDownloadReport();
  const queryParams = new URLSearchParams(location.search);

  // Suite mode params
  const tsrId = queryParams.get("tsrId");
  const runDate = queryParams.get("date");
  const name = queryParams.get("name");

  // Test case mode params
  const tcrid = queryParams.get("tcrid");
  const tcid = queryParams.get("tcid");
  const tcname = queryParams.get("tcname");
  const mode = queryParams.get("mode"); // "multirun" | "designprincipals"

  const isTestCaseMode = !!tcrid && !tsrId;
  const isMultiRun = isTestCaseMode && mode === "multirun";
  const isDesignPrincipals = isTestCaseMode && mode === "designprincipals";
  const isStepDesignPrincipals = isTestCaseMode && mode === "stepdesignprincipals";

  const [selectedStepId, setSelectedStepId] = useState("");

  const { data, isSuccess } = useBenchmarkResultsQuery(tsrId, {
    enabled: Boolean(tsrId),
  });

  const { data: designReportData } = useGetUIDesignReportQuery(tcrid, {
    enabled: isStepDesignPrincipals && !!tcrid,
    principleLevel: "Test_step",
  });
  const stepOptions = designReportData?.rows ?? [];

  const suiteBreadcrumbs = [
    <Typography key="1">Execution Reports</Typography>,
    <Typography key="2">{`Test Suite - ${name}`}</Typography>,
    <Typography key="3">{`Run date - ${runDate}`}</Typography>,
    <Typography key="4">Benchmark Report</Typography>,
  ];

  const testCaseBreadcrumbs = [
    <Typography key="1">Execution Reports</Typography>,
    <Typography key="2">{`Test Case - ${tcname}`}</Typography>,
    <Typography key="3">{`Run date - ${runDate}`}</Typography>,
    <Typography key="4">
      {isMultiRun ? "Multi-Run Results" : "Design Principal Results"}
    </Typography>,
  ];

  const pageTitle = isMultiRun
    ? `Multi-Run Results for ${tcname}`
    : isDesignPrincipals
      ? `Design Principal Results for ${tcname}`
      : isStepDesignPrincipals
        ? `Step Design Principal Results for ${tcname}`
        : `Benchmark Report for ${name}`;

  const [anchorEl, setAnchorEl] = useState(null);

  const handleDownloadClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleDownload = (format) => {
    const principleLevel = mode === "stepdesignprincipals" ? "Test_step" : "Test_case";

    downloadReport(
      { tcrid, tcid, format, principleLevel, testStepId: selectedStepId },
      {
        onSuccess: (response) => {
          const { data, headers } = response;

          const contentDisposition = headers["content-disposition"];
          let fileName = format === "excel" ? "Report.xlsx" : "Report.pdf";

          if (contentDisposition) {
            const match = contentDisposition.match(/filename="(.+)"/);
            if (match) fileName = match[1];
          }

          const blob = new Blob([data], {
            type:
              format === "excel"
                ? "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                : "application/pdf",
          });

          const url = window.URL.createObjectURL(blob);

          const a = document.createElement("a");
          a.href = url;
          a.setAttribute("download", fileName);
          a.style.display = "none";

          document.body.appendChild(a);
          a.click();

          setTimeout(() => {
            document.body.removeChild(a);
            window.URL.revokeObjectURL(url);
          }, 100);
        },

        onError: (error) => {
          console.error("Download failed:", error);
        },
      },
    );
  };
  return (
    <Box sx={{ my: 3 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
        <PageTitle title={pageTitle} variant="h5" />

        {isTestCaseMode && (
          <>
            <Button variant="contained" onClick={handleDownloadClick} disabled={loading}>
              {loading ? "Downloading..." : "Download Reports"}
            </Button>

            <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleClose}>
              <MenuItem onClick={() => handleDownload("excel")}>Download Excel</MenuItem>

              <MenuItem onClick={() => handleDownload("pdf")}>Download PDF</MenuItem>
            </Menu>
          </>
        )}
      </Box>

      <CustomBreadcrumbs ariaLabel="breadcrumb" mb={2}>
        {isTestCaseMode ? testCaseBreadcrumbs : suiteBreadcrumbs}
      </CustomBreadcrumbs>

      {isMultiRun && (
        <>
          <Tabs value={tab} onChange={(e, v) => setTab(v)} sx={{ mb: 3 }}>
            <Tab label="Multi-Run Results" />
          </Tabs>
          <MultiRunTable tcrid={tcrid} />
        </>
      )}

      {isDesignPrincipals && (
        <>
          <Tabs value={tab} onChange={(e, v) => setTab(v)} sx={{ mb: 3 }}>
            <Tab label="Design Principal Results" />
          </Tabs>
          <DesignPrincipalTable tcrid={tcrid} principleLevel="Test_case" />
        </>
      )}

      {isStepDesignPrincipals && (
        <>
          <Tabs value={tab} onChange={(e, v) => setTab(v)} sx={{ mb: 3 }}>
            <Tab label="Step Design Principal Results" />
          </Tabs>

          <FormControl size="small" sx={{ minWidth: 220, mb: 2 }}>
            <InputLabel id="step-select-label">Step</InputLabel>
            <Select
              labelId="step-select-label"
              label="Step"
              value={selectedStepId}
              onChange={(e) => setSelectedStepId(e.target.value)}
            >
              <MenuItem value="">All Steps</MenuItem>
              {stepOptions.map((row) => (
                <MenuItem key={row.testStepId} value={row.testStepId}>
                  Step {row.stepNumber}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {selectedStepId ? (
            <DesignPrincipalTable
              tcrid={tcrid}
              testStepId={selectedStepId}
              principleLevel="Test_step"
            />
          ) : (
            <DesignPrincipalTable tcrid={tcrid} principleLevel="Test_step" />
          )}
        </>
      )}

      {!isTestCaseMode && (
        <>
          <Tabs value={tab} onChange={(e, v) => setTab(v)} sx={{ mb: 2 }}>
            <Tab label="Smoke Test Results" />
            <Tab label="Milestone Performance" />
            <Tab label="Summary Report" />
          </Tabs>
          {tab === 0 && <SmokeTestResults data={data} isSuccess={isSuccess} />}
          {tab === 1 && <MilestonePerformance data={data} tsrId={tsrId} />}
          {tab === 2 && <SummaryReport tsrId={tsrId} />}
        </>
      )}
    </Box>
  );
};

export default BenchmarkReport;
